import type { Map as MapLibreMap } from 'maplibre-gl';
import { EMPTY_FIELD, sampleField, type WindField } from '../model/windField';
import { frameLngLat, ViewCanvas, type ViewFrame } from './viewCanvas';

/** Screen speed of a particle per m/s of wind, px/s: 10 m/s crosses a phone screen in about 10 s. */
const PX_PER_MS = 4;
/** Screen area per particle, px². */
const AREA_PER_PARTICLE = 650;
const MAX_PARTICLES = 6000;
/** A particle lives this long (randomised ±50%), s, then starts again somewhere else. */
const LIFETIME = 3;
/** Share of a trail's opacity kept per 1/60 s: lower fades trails faster. */
const TRAIL_KEEP = 0.94;
/** Line width, screen px. */
const LINE_WIDTH = 1.5;
const COLOR = 'rgba(255, 255, 255, 0.9)';
/** Canvas pixel budget: the canvas is uploaded to the GPU every frame. */
const MAX_PIXELS = 3_000_000;
/** A frame longer than this (a hidden tab, a hiccup) counts as this, s. */
const MAX_DT = 0.05;

/**
 * Animated particles following the wind, like windy.com: short-lived white streaks whose trails fade.
 * When the canvas follows the view, particles and trails move along with it.
 */
export class WindParticles {
  private readonly view: ViewCanvas;
  /** Per particle: x, y (canvas px) and age (s). */
  private particles = new Float32Array(0);
  private lifetimes = new Float32Array(0);
  private field: WindField = EMPTY_FIELD;
  private running = false;
  private raf = 0;
  private last = 0;
  private readonly wind: [number, number] = [0, 0];
  private readonly lngLat: [number, number] = [0, 0];

  constructor(map: MapLibreMap, options: { id: string; beforeId?: string }) {
    this.view = new ViewCanvas(map, {
      ...options,
      opacity: 1,
      padding: 0.25,
      resolution: (screenPx) => Math.min(devicePixelRatio, Math.sqrt(MAX_PIXELS / screenPx)),
      keepPicture: true,
      onFit: (frame, previous) => this.onFit(frame, previous),
    });
  }

  setField(field: WindField) {
    this.field = field;
  }

  setRunning(running: boolean) {
    if (running === this.running) return;
    this.running = running;
    this.view.setRunning(running);
    if (running) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    } else {
      cancelAnimationFrame(this.raf);
    }
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.view.dispose();
  }

  /**
   * Keeps particles where they were on the map (if carrying on), thinned after zooming out so they don't
   * crowd into the old view's area, and adds new ones up to the count for the new size.
   */
  private onFit(frame: ViewFrame, previous: ViewFrame | null) {
    const { width, height } = this.view.canvas;
    const screenArea = (width / frame.scale) * (height / frame.scale);
    const count = Math.min(MAX_PARTICLES, Math.round(screenArea / AREA_PER_PARTICLE));
    const particles = new Float32Array(count * 3);
    const lifetimes = new Float32Array(count);
    let kept = 0;
    if (previous) {
      // Old canvas pixels → new: through Web Mercator.
      const k = previous.unit / frame.unit;
      const dx = (previous.x0 - frame.x0) / frame.unit;
      const dy = (previous.y0 - frame.y0) / frame.unit;
      // Zooming out by k packs particles k² as densely on screen.
      const share = Math.min(1, k * k);
      for (let p = 0; p < this.lifetimes.length && kept < count; p++) {
        if (Math.random() >= share) continue;
        particles[kept * 3] = dx + this.particles[p * 3] * k;
        particles[kept * 3 + 1] = dy + this.particles[p * 3 + 1] * k;
        particles[kept * 3 + 2] = this.particles[p * 3 + 2];
        lifetimes[kept] = this.lifetimes[p];
        kept++;
      }
    }
    this.particles = particles;
    this.lifetimes = lifetimes;
    for (let p = kept; p < count; p++) {
      lifetimes[p] = LIFETIME * (0.5 + Math.random());
      this.respawn(p);
      // Staggered, so they don't all restart at once.
      particles[p * 3 + 2] = Math.random() * lifetimes[p];
    }
  }

  private respawn(p: number) {
    this.particles[p * 3] = Math.random() * this.view.canvas.width;
    this.particles[p * 3 + 1] = Math.random() * this.view.canvas.height;
    this.particles[p * 3 + 2] = 0;
  }

  private readonly tick = (now: number) => {
    this.raf = requestAnimationFrame(this.tick);
    const dt = Math.min(MAX_DT, (now - this.last) / 1000);
    this.last = now;
    const { field, particles, wind, lngLat } = this;
    const { canvas, ctx, frame } = this.view;

    // Fade the trails drawn so far.
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = `rgba(0, 0, 0, ${TRAIL_KEEP ** (dt * 60)})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';

    const step = PX_PER_MS * frame.scale * dt;
    ctx.beginPath();
    for (let p = 0; p < this.lifetimes.length; p++) {
      const i = p * 3;
      const x = particles[i];
      const y = particles[i + 1];
      particles[i + 2] += dt;
      frameLngLat(frame, x, y, lngLat);
      if (particles[i + 2] > this.lifetimes[p] || !sampleField(field, lngLat[0], lngLat[1], wind)) {
        this.respawn(p);
        continue;
      }
      const nx = x + wind[0] * step;
      const ny = y - wind[1] * step;
      if (nx < 0 || ny < 0 || nx > canvas.width || ny > canvas.height) {
        this.respawn(p);
        continue;
      }
      ctx.moveTo(x, y);
      ctx.lineTo(nx, ny);
      particles[i] = nx;
      particles[i + 1] = ny;
    }
    ctx.strokeStyle = COLOR;
    ctx.lineWidth = Math.max(0.75, LINE_WIDTH * frame.scale);
    ctx.lineCap = 'round';
    ctx.stroke();
  };
}
